import type { VariationStrategy } from "../types";
import { IconCheck } from "./icons";
import { cx } from "./ui";

/**
 * バリエーション戦略の選択カード。
 * 「同じプロンプトでN枚」か、軸を持つ戦略で1枚ごとに指示を散らすかを選ぶ。
 */
export function VariationPicker({
  strategies,
  selected,
  variantsPer,
  onSelect,
}: {
  strategies: VariationStrategy[];
  selected: string;
  variantsPer: number;
  onSelect: (s: VariationStrategy) => void;
}) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {strategies.map((s) => {
        const on = s.id === selected;
        // いまの案数でどの軸まで使われるかを見せる
        const usedAxes = s.axes.slice(0, Math.min(variantsPer, s.axes.length));
        return (
          <button
            key={s.id}
            type="button"
            onClick={() => onSelect(s)}
            aria-pressed={on}
            className={cx(
              "relative flex flex-col items-start gap-1 rounded-xl border-2 p-3 text-left transition",
              on
                ? "border-brand-500 bg-brand-50 shadow-[0_0_0_3px_rgba(124,92,255,0.12)]"
                : "border-slate-200 bg-white hover:border-brand-300 hover:bg-brand-50/40",
            )}
          >
            <div className="flex w-full items-center gap-2">
              <span className="text-[16px] leading-none" aria-hidden>
                {s.emoji}
              </span>
              <span className={cx("text-[13.5px] font-bold", on ? "text-brand-700" : "text-slate-700")}>
                {s.label}
              </span>
              {on && (
                <span className="ml-auto grid h-4 w-4 shrink-0 place-items-center rounded-full bg-brand-500 text-white">
                  <IconCheck className="h-2.5 w-2.5" />
                </span>
              )}
            </div>
            <p className="text-[11.5px] leading-relaxed text-slate-400">{s.hint}</p>
            {on && s.axes.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {usedAxes.map((a) => (
                  <span
                    key={a.label}
                    className="rounded bg-white px-1.5 py-0.5 text-[12px] font-semibold text-brand-700"
                  >
                    {a.label}
                  </span>
                ))}
                {variantsPer > s.axes.length && (
                  <span className="rounded px-1 py-0.5 text-[12px] text-slate-400">×繰り返し</span>
                )}
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}
